import React, { useState, useEffect, useRef } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Popover,
  Typography,
  Avatar,
  Chip,
  Divider,
  CircularProgress,
} from "@material-ui/core";
import api from "../../services/api";
import { format, parseISO } from "date-fns";

const useStyles = makeStyles((theme) => ({
  popover: {
    pointerEvents: "none",
  },
  paper: {
    pointerEvents: "auto",
    padding: theme.spacing(2),
    maxWidth: 360,
    minWidth: 280,
    borderRadius: 12,
    boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
  },
  avatar: {
    width: 40,
    height: 40,
  },
  name: {
    fontWeight: 600,
    fontSize: "0.95rem",
  },
  number: {
    fontSize: "0.78rem",
    color: theme.palette.text.secondary,
  },
  metaRow: {
    display: "flex",
    gap: 6,
    flexWrap: "wrap",
    marginBottom: theme.spacing(1),
  },
  chip: {
    height: 22,
    fontSize: "0.72rem",
  },
  messagesSection: {
    marginTop: theme.spacing(1),
  },
  messageItem: {
    padding: theme.spacing(0.75, 1),
    borderRadius: 8,
    marginBottom: theme.spacing(0.5),
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,0.05)"
        : "rgba(0,0,0,0.03)",
  },
  messageBody: {
    fontSize: "0.8rem",
    color: theme.palette.text.primary,
    wordBreak: "break-word",
  },
  messageTime: {
    fontSize: "0.68rem",
    color: theme.palette.text.disabled,
    marginTop: 2,
  },
  loadingBox: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(2),
  },
  sectionLabel: {
    fontSize: "0.72rem",
    fontWeight: 600,
    color: theme.palette.text.secondary,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    marginBottom: 4,
  },
}));

const KanbanCardPreview = ({ ticket, anchorEl, onClose }) => {
  const classes = useStyles();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const timerRef = useRef(null);
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (ticket && anchorEl) {
      timerRef.current = setTimeout(() => setShow(true), 500);
    } else {
      setShow(false);
      setMessages([]);
    }
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [ticket, anchorEl]);

  useEffect(() => {
    if (!show || !ticket?.id) return;
    let cancelled = false;
    setLoading(true);
    api
      .get(`/messages/${ticket.id}`, { params: { pageNumber: 1 } })
      .then(({ data }) => {
        if (!cancelled) {
          const msgs = (data?.messages || data || []).slice(0, 5);
          setMessages(msgs);
        }
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [show, ticket?.id]);

  if (!show || !ticket || !anchorEl) return null;

  const contact = ticket.contact || {};
  const queue = ticket.queue;
  const assignee = ticket.user;

  return (
    <Popover
      className={classes.popover}
      classes={{ paper: classes.paper }}
      open={true}
      anchorEl={anchorEl}
      anchorOrigin={{ vertical: "center", horizontal: "right" }}
      transformOrigin={{ vertical: "center", horizontal: "left" }}
      onClose={onClose}
      disableRestoreFocus
    >
      {/* Header */}
      <div className={classes.header}>
        <Avatar
          className={classes.avatar}
          src={contact.urlPicture || undefined}
        />
        <div>
          <Typography className={classes.name}>
            {contact.name || "Sem nome"}
          </Typography>
          <Typography className={classes.number}>
            {contact.number || ""}
          </Typography>
        </div>
      </div>

      {/* Contact details */}
      {contact.email && (
        <Typography variant="body2" color="textSecondary" style={{ fontSize: "0.75rem", marginBottom: 4 }}>
          {contact.email}
        </Typography>
      )}

      {/* Meta */}
      <div className={classes.metaRow}>
        <Chip
          className={classes.chip}
          label={ticket.status}
          size="small"
          color={
            ticket.status === "open"
              ? "primary"
              : ticket.status === "pending"
              ? "default"
              : "default"
          }
        />
        {queue && (
          <Chip
            className={classes.chip}
            label={queue.name}
            size="small"
            style={{ backgroundColor: queue.color, color: "#fff" }}
          />
        )}
        {assignee && (
          <Chip
            className={classes.chip}
            label={assignee.name}
            size="small"
            variant="outlined"
          />
        )}
        {ticket.priority && ticket.priority !== "normal" && (
          <Chip
            className={classes.chip}
            label={ticket.priority}
            size="small"
            variant="outlined"
            style={{ textTransform: "capitalize" }}
          />
        )}
      </div>

      {/* Wallet */}
      {contact.ContactWallets && contact.ContactWallets.length > 0 && (
        <div className={classes.metaRow} style={{ marginTop: 4 }}>
          <Typography variant="caption" color="textSecondary" style={{ marginRight: 4 }}>
            Carteira:
          </Typography>
          {contact.ContactWallets.map((w, idx) => (
            <Chip
              key={idx}
              className={classes.chip}
              label={w.user?.name || `ID ${w.walletId}`}
              size="small"
              variant="outlined"
            />
          ))}
        </div>
      )}

      <Divider />

      {/* Messages */}
      <div className={classes.messagesSection}>
        <Typography className={classes.sectionLabel}>
          Últimas mensagens
        </Typography>
        {loading ? (
          <div className={classes.loadingBox}>
            <CircularProgress size={20} />
          </div>
        ) : messages.length === 0 ? (
          <Typography
            variant="body2"
            color="textSecondary"
            style={{ fontSize: "0.8rem" }}
          >
            Nenhuma mensagem
          </Typography>
        ) : (
          messages.map((msg) => (
            <div key={msg.id} className={classes.messageItem}>
              <Typography className={classes.messageBody}>
                {(msg.body || "").substring(0, 150)}
                {(msg.body || "").length > 150 ? "..." : ""}
              </Typography>
              <Typography className={classes.messageTime}>
                {msg.createdAt
                  ? format(parseISO(msg.createdAt), "dd/MM HH:mm")
                  : ""}
              </Typography>
            </div>
          ))
        )}
      </div>
    </Popover>
  );
};

export default KanbanCardPreview;
